'use client'

import * as React from 'react'
import { CalendarIcon } from '@radix-ui/react-icons'
import { format } from 'date-fns'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button/button'
import { Calendar } from '@/components/ui/calander/calender'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover/popover'
import { FormItem, FormLabel } from '../form/form'
import { FormMode } from '@/lib/common-models'
import { ControllerRenderProps, Path } from 'react-hook-form'
import { TypeOf, ZodSchema } from 'zod'
import { toZonedTime } from 'date-fns-tz'
import { useState } from 'react'

interface DateInputProps<T extends ZodSchema, K extends Path<TypeOf<T>>> {
  label: string
  range?: 'single' | 'range' | 'multiple' | 'default'
  mode?: FormMode
  field: ControllerRenderProps<TypeOf<T>, K>
  disabledCondition?: (value: Date) => boolean
  className?: string
  calenderClass?: string
  defaultMonth?: Date
  readOnly?: boolean
  formatType?: 'day' | 'month'
  /**
   * Transaction name for RUM monitoring.
   * Required for observability - tracks user interactions in Elastic APM.
   * Format: area.page.field_name (e.g., "forms.fd02.dob")
   */
  transactionName: string
  closeOnSelect?: boolean
}

export const DateInput = <T extends ZodSchema, K extends Path<TypeOf<T>>>({
  field,
  label,
  range = 'single',
  formatType = 'day',
  disabledCondition,
  className,
  calenderClass,
  defaultMonth,
  readOnly = false,
  transactionName,
  closeOnSelect = false,
}: DateInputProps<T, K>) => {
  const [open, setOpen] = useState(false)

  const calendarMode = range === 'range' ? 'range' : 'single'

  function renderEditMode() {
    // Parse the field value to get a proper Date object
    const fieldDate = field.value ? new Date(field.value) : undefined

    // For month picker, ensure we're working with the 1st of the month
    const selectedDate =
      fieldDate && formatType === 'month'
        ? new Date(fieldDate.getFullYear(), fieldDate.getMonth(), 1)
        : fieldDate

    const dateFormat = formatType === 'month' ? 'MM/yyyy' : 'dd/MM/yyyy'

    const handleDateChange = (date: Date) => {
      const timeZone = 'Asia/Kolkata'
      const zonedDate = toZonedTime(date, timeZone)

      const formattedDate = format(zonedDate, "yyyy-MM-dd'T'HH:mm:ss.SSS")

      field.onChange(formattedDate)

      /**
       * Close popover only for single mode
       * Prevents breaking future range selection
       */
      if (closeOnSelect && calendarMode === 'single') {
        setOpen(false)
      }

      field.onBlur()
    }

    return (
      <FormItem className="relative flex animate-fade-in flex-col">
        <FormLabel
          className={cn(
            'absolute left-0 top-2 z-10 -translate-y-1/2 translate-x-4 bg-white px-2',
            readOnly
              ? 'cursor-not-allowed bg-bgNeutral text-gray-500'
              : 'text-gray-600'
          )}
        >
          {' '}
          {label}
        </FormLabel>
        <Popover
          open={open}
          onOpenChange={(isOpen) => {
            setOpen(isOpen)
            if (!isOpen) {
              field.onBlur()
            }
          }}
        >
          <PopoverTrigger asChild disabled={readOnly}>
            <Button
              variant={'outline'}
              className={cn(
                'my-4 max-w-[22rem] justify-between p-6 text-left font-semibold tracking-10',
                className,
                !field.value && 'text-muted-foreground'
              )}
              transactionName="date-input.open-calendar"
            >
              {field.value ? (
                format(new Date(field.value), dateFormat)
              ) : (
                <span></span>
              )}

              <CalendarIcon className="mr-2 size-4 text-blue-600" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              className={calenderClass ?? ''}
              mode="single"
              selected={selectedDate}
              onSelect={(value: Date | undefined) => {
                if (!value) return

                const finalDate =
                  formatType === 'month'
                    ? new Date(value.getFullYear(), value.getMonth(), 1)
                    : value

                handleDateChange(finalDate)
              }}
              onMonthChange={(month: Date) => {
                if (formatType !== 'month') return

                const finalDate = new Date(
                  month.getFullYear(),
                  month.getMonth(),
                  1
                )

                handleDateChange(finalDate)
              }}
              disabled={disabledCondition}
              autoFocus
              defaultMonth={defaultMonth || selectedDate}
              formatType={formatType}
            />
          </PopoverContent>
        </Popover>
        <input
          type="hidden"
          {...field}
          data-transaction-name={transactionName}
        />
      </FormItem>
    )
  }
  // TO-DO: Date Input Should Be Able to Take Date Range
  return <>{renderEditMode()}</>
}
